/**
 * Free-text classes (LBL-08): the class a user names in a tool for a finding no catalog label names.
 * The name they type, spelled as the catalog's names are, is the object's label `free_text:<name>`.
 * Free-text classes share one color and have no attributes or key; `labelName` marks them wherever
 * they show, in quotes, and they never join the catalog.
 */
import type { Label, LabelType } from '../lib/types'

const PREFIX = 'free_text:'
const NAME = /^[a-z0-9_]{1,64}$/
/** The one color of every free-text class, as the API's. */
export const FREE_TEXT_COLOR = '#ff9a3c'
/** The color of a shape whose label is not known here; written in full, as masks are painted from #rrggbb. */
export const NO_LABEL_COLOR = '#ffffff'

export const isFreeText = (label: string) => label.startsWith(PREFIX) && NAME.test(label.slice(PREFIX.length))
export const freeTextLabel = (name: string) => `${PREFIX}${name}`
export const freeTextName = (label: string) => label.slice(PREFIX.length)

// Letters spelled out in names; other accents are dropped, and anything else separates words.
const SPELLED: Readonly<Record<string, string>> = { ä: 'ae', ö: 'oe', ü: 'ue', ß: 'ss', æ: 'ae', œ: 'oe', ø: 'o', ł: 'l', đ: 'd', ð: 'd', þ: 'th' }

/**
 * What the user typed as the catalog writes names: lowercase letters a to z, digits and underscores,
 * at most 64 characters; spaces and other signs become one underscore, ä is spelled ae and é e.
 * Empty where nothing of it remains. The migration of the free-text labels before spells alike.
 */
export function spelled(typed: string): string {
  const lower = [...typed.normalize('NFC').toLowerCase()].map((char) => SPELLED[char] ?? char).join('')
  const plain = lower.normalize('NFD').replace(/\p{M}/gu, '')
  return plain.replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 64).replace(/_+$/, '')
}

/** A free-text class as a label of a shape type, or of image tags. */
export function freeTextClass(label: string, type: LabelType): Label {
  return { name: label, type, color: FREE_TEXT_COLOR, attributes: [], free_text: true }
}

/**
 * Labels by name that know every free-text class too: one looked up is made and kept, so whatever
 * shows annotations finds its color and (no) attributes as for any label. Made by name alone, it
 * says image tag as its type; the object's own type is what counts.
 */
class WithFreeText<V> extends Map<string, V> {
  readonly #made: (label: string) => V

  constructor(entries: Iterable<readonly [string, V]>, made: (label: string) => V) {
    super(entries)
    this.#made = made
  }

  override get(name: string): V | undefined {
    const found = super.get(name)
    if (found !== undefined || !isFreeText(name)) return found
    const made = this.#made(name)
    this.set(name, made)
    return made
  }
}

export const labelsByName = (labels: readonly Label[]): ReadonlyMap<string, Label> =>
  new WithFreeText(labels.map((label) => [label.name, label]), (name) => freeTextClass(name, 'tag'))

export const colorsByName = (labels: readonly Label[]): ReadonlyMap<string, string> =>
  new WithFreeText(labels.map((label) => [label.name, label.color]), () => FREE_TEXT_COLOR)
